import { useState, useEffect } from "react";
import mermaid from "mermaid";

let mermaidInitialized = false;

export const initMermaid = () => {
  if (mermaidInitialized) return;
  mermaid.initialize({
    startOnLoad: false,
    theme: "default",
    // "strict": mermaid sanitizes label HTML and disables click/JS callbacks.
    // "loose" skipped that and let AI-generated diagram code inject markup (UI-1).
    securityLevel: "strict",
    fontFamily:
      'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    logLevel: "error",
    suppressErrorRendering: true,
  });
  mermaidInitialized = true;
};

const fixReverseArrows = (code) => {
  return code.replace(
    /^(\s*)([\w[\]"'(){}|`. ]+?)\s+(<[-=.]+)\s+([\w[\]"'(){}|`. ]+?)\s*$/gm,
    (_match, indent, leftNode, arrow, rightNode) => {
      const reversedArrow = arrow
        .split("")
        .reverse()
        .map((ch) => (ch === "<" ? ">" : ch))
        .join("");
      return `${indent}${rightNode.trim()} ${reversedArrow} ${leftNode.trim()}`;
    },
  );
};

export const sanitizeMermaidCode = (code) => {
  if (!code) return code;
  let sanitized = code;
  sanitized = fixReverseArrows(sanitized);
  sanitized = sanitized.replace(/;\s*$/gm, "");
  sanitized = sanitized.replace(/\n{3,}/g, "\n\n");

  // Fix AI-generated casing issues: SubGraph → subgraph, End → end
  // Mermaid keywords are case-sensitive and must be lowercase.
  sanitized = sanitized.replace(
    /^(\s*)SubGraph\b/gim,
    (_m, indent) => `${indent}subgraph`,
  );
  sanitized = sanitized.replace(
    /^(\s*)End\s*$/gim,
    (_m, indent) => `${indent}end`,
  );

  // Auto-quote unquoted subgraph labels that contain spaces or parentheses.
  // e.g. "subgraph Agent Tools (MCP)" → 'subgraph "Agent Tools (MCP)"'
  sanitized = sanitized.replace(
    /^(\s*subgraph)\s+(?!["'])([A-Za-z][\w]*(?:\s+[^\n"']+))\s*$/gm,
    (_m, keyword, label) => `${keyword} "${label.trim()}"`,
  );

  // ─── Comprehensive node-label auto-quoting ───
  // AI-generated mermaid often includes special characters (parentheses,
  // slashes, colons, ampersands, etc.) inside node labels.  Mermaid
  // interprets many of these as syntax tokens and fails to parse.
  // Wrapping every unquoted label in double-quotes makes them safe.
  //
  // Covers: A[text] → A["text"],  D{text} → D{"text"}
  // Skips labels that are already quoted (contain ").

  // Square bracket labels: A[...] → A["..."]
  sanitized = sanitized.replace(
    /(\w+)\[([^\]"]+)\]/g,
    (_m, nodeId, label) => `${nodeId}["${label}"]`,
  );

  // Curly brace (diamond) labels: D{...} → D{"..."}
  sanitized = sanitized.replace(
    /(\w+)\{([^}"]+)\}/g,
    (_m, nodeId, label) => `${nodeId}{"${label}"}`,
  );

  return sanitized;
};

export const parseMermaidToText = (code) => {
  if (!code) return { diagramType: "Diagram", relationships: [] };

  const lines = code.trim().split("\n");
  const relationships = [];
  let diagramType = "Diagram";

  const firstLine = lines[0]?.trim().toLowerCase() || "";
  if (firstLine.startsWith("flowchart") || firstLine.startsWith("graph")) {
    diagramType = "Flowchart";
  } else if (firstLine.startsWith("sequencediagram")) {
    diagramType = "Sequence Diagram";
  } else if (firstLine.startsWith("classdiagram")) {
    diagramType = "Class Diagram";
  } else if (firstLine.startsWith("statediagram")) {
    diagramType = "State Diagram";
  } else if (firstLine.startsWith("erdiagram")) {
    diagramType = "ER Diagram";
  } else if (firstLine.startsWith("gantt")) {
    diagramType = "Gantt Chart";
  } else if (firstLine.startsWith("pie")) {
    diagramType = "Pie Chart";
  } else if (firstLine.startsWith("mindmap")) {
    diagramType = "Mind Map";
  } else if (firstLine.startsWith("timeline")) {
    diagramType = "Timeline";
  } else if (firstLine.startsWith("journey")) {
    diagramType = "User Journey";
  } else if (firstLine.startsWith("gitgraph")) {
    diagramType = "Git Graph";
  }

  const nodeLabels = {};

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line || line.startsWith("%%")) continue;

    const nodeDef = line.match(/(\w+)\s*\[\s*["']?([^"'\]]+)["']?\s*\]/g);
    if (nodeDef) {
      for (const def of nodeDef) {
        const m = def.match(/(\w+)\s*\[\s*["']?([^"'\]]+)["']?\s*\]/);
        if (m) nodeLabels[m[1]] = m[2];
      }
    }

    const relMatch = line.match(
      /(\w+(?:\[.*?\])?)\s*([<\-=.>|]+)\s*(\w+(?:\[.*?\])?)/,
    );
    if (relMatch) {
      const [, leftRaw, arrow, rightRaw] = relMatch;
      const leftId = leftRaw.replace(/\[.*?\]/, "").trim();
      const rightId = rightRaw.replace(/\[.*?\]/, "").trim();
      const leftLabel = nodeLabels[leftId] || leftId;
      const rightLabel = nodeLabels[rightId] || rightId;

      if (arrow.includes("<") && arrow.includes(">")) {
        relationships.push(`${leftLabel} ↔ ${rightLabel}`);
      } else if (arrow.includes(">")) {
        relationships.push(`${leftLabel} → ${rightLabel}`);
      } else if (arrow.includes("<")) {
        relationships.push(`${rightLabel} → ${leftLabel}`);
      } else {
        relationships.push(`${leftLabel} — ${rightLabel}`);
      }
    }
  }

  return { diagramType, relationships: [...new Set(relationships)] };
};

let renderCounter = 0;

const useMermaid = (code) => {
  const [svg, setSvg] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!code || !code.trim()) {
      setSvg(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const diagramId = `mermaid-diagram-${++renderCounter}`;

    const renderDiagram = async () => {
      setLoading(true);
      setError(null);

      let sanitizedCode = "";
      try {
        initMermaid();
        sanitizedCode = sanitizeMermaidCode(code.trim());
        const { svg: renderedSvg } = await mermaid.render(diagramId, sanitizedCode);

        if (!cancelled) {
          setSvg(renderedSvg);
          setError(null);
        }
      } catch (err) {
        console.error("[MermaidBlock] Render failed:", err?.message || err);
        console.error("[MermaidBlock] Sanitized code was:\n", sanitizedCode);
        if (!cancelled) {
          setSvg(null);
          setError(err?.message || "Unable to render diagram");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    renderDiagram();

    return () => {
      cancelled = true;
    };
  }, [code]);

  return { svg, error, loading };
};

export default useMermaid;
